import React, { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown, X } from "lucide-react";
import type { DateRange } from "../../types/dashboard.types";

interface Props {
  activePeriod: number | null;
  dateRange: DateRange;
  onChange: (range: DateRange, period?: number) => void;
}

const QUICK_PERIODS = [
  { label: "7j",   days: 7  },
  { label: "30j",  days: 30 },
  { label: "90j",  days: 90 },
  { label: "1 an", days: 365 },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo  = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtDisplay = (s: string) =>
  new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

const DateInput: React.FC<{
  label: string;
  value: string;
  max?: string;
  min?: string;
  onChange: (v: string) => void;
}> = ({ label, value, max, min, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
      {label}
    </label>
    <input
      type="date"
      value={value}
      max={max}
      min={min}
      onChange={e => onChange(e.target.value)}
      className="px-2.5 py-1.5 rounded-lg text-[12px] font-mono text-slate-700
        border border-slate-200 bg-white focus:outline-none focus:border-blue-400
        focus:ring-2 focus:ring-blue-100 transition-all cursor-pointer"
    />
  </div>
);

const PeriodSelector: React.FC<Props> = ({ activePeriod, dateRange, onChange }) => {
  const [open,      setOpen]      = useState(false);
  const [customStart, setCustomStart] = useState(dateRange.startDate);
  const [customEnd,   setCustomEnd]   = useState(dateRange.endDate);
  const ref = useRef<HTMLDivElement>(null);

  // Sync local state when dateRange changes externally
  useEffect(() => {
    setCustomStart(dateRange.startDate);
    setCustomEnd(dateRange.endDate);
  }, [dateRange.startDate, dateRange.endDate]);

  // Close on outside click
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const handleQuickPeriod = (days: number) => {
    const range = { startDate: daysAgo(days - 1), endDate: todayStr() };
    onChange(range, days);
    setOpen(false);
  };

  const handleApplyCustom = () => {
    if (!customStart || !customEnd) return;
    if (customStart > customEnd) return;
    onChange({ startDate: customStart, endDate: customEnd }, undefined);
    setOpen(false);
  };

  // Label shown on trigger button
  const triggerLabel = activePeriod
    ? `${activePeriod}j`
    : `${fmtDisplay(dateRange.startDate)} – ${fmtDisplay(dateRange.endDate)}`;

  const isCustomActive = !activePeriod;

  return (
    <div className="relative" ref={ref}>

      {/* ── Trigger ── */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold
          border transition-all duration-150 cursor-pointer
          ${open
            ? "bg-[#0F52BA] text-white border-[#0F52BA] shadow-sm"
            : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700"
          }`}
      >
        <Calendar size={12} />
        <span>{triggerLabel}</span>
        <ChevronDown
          size={11}
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 w-72
          bg-white rounded-2xl border border-slate-100
          shadow-xl shadow-slate-200/60
          animate-[fadeIn_0.15s_ease]">

          {/* Quick periods */}
          <div className="px-4 pt-4 pb-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
              Période rapide
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {QUICK_PERIODS.map(({ label, days }) => (
                <button
                  key={days}
                  onClick={() => handleQuickPeriod(days)}
                  className={`py-1.5 rounded-lg text-[11px] font-semibold border transition-all
                    ${activePeriod === days
                      ? "bg-[#0F52BA] text-white border-[#0F52BA] shadow-sm"
                      : "bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="mx-4 border-t border-slate-100" />

          {/* Custom date range */}
          <div className="px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
              Période personnalisée
            </p>

            <div className="flex items-end gap-2">
              <DateInput
                label="Du"
                value={customStart}
                max={customEnd || todayStr()}
                onChange={setCustomStart}
              />
              <span className="text-slate-300 text-sm pb-1.5 flex-shrink-0">→</span>
              <DateInput
                label="Au"
                value={customEnd}
                min={customStart}
                max={todayStr()}
                onChange={setCustomEnd}
              />
            </div>

            {/* Validation feedback */}
            {customStart && customEnd && customStart > customEnd && (
              <p className="mt-2 text-[10px] text-rose-500 flex items-center gap-1">
                <X size={10} /> La date de fin doit être après le début
              </p>
            )}

            <button
              onClick={handleApplyCustom}
              disabled={!customStart || !customEnd || customStart > customEnd}
              className="mt-3 w-full py-2 rounded-xl text-[12px] font-semibold
                bg-[#0F52BA] text-white hover:bg-blue-700 transition-colors
                disabled:opacity-40 disabled:cursor-not-allowed shadow-sm shadow-blue-200"
            >
              Appliquer
            </button>
          </div>

          {/* Active range display */}
          {isCustomActive && (
            <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-[10px] text-blue-600 font-medium">
                Période active : {fmtDisplay(dateRange.startDate)} → {fmtDisplay(dateRange.endDate)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;